import { useState, useMemo } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  CreditCard, History, Trash2, Plus, Calendar as CalendarIcon, FileText,
} from "lucide-react";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { toast } from "sonner";

const INVOICE_TYPE_LABELS = {
  acompte: "Facture d'acompte",
  situation: "Situation",
  finale: "Facture finale",
  autre: "Autre",
};

export default function PaymentsDialog({
  open, onOpenChange,
  order,
  orderPayments,
  newPayment, setNewPayment,
  onAddPayment, onDeletePayment,
  onRefresh,
}) {
  const totalTtc = order?.amount_ttc ?? (order?.amount_ht || 0) * (1 + (order?.vat_rate ?? 20) / 100);
  const progressPct = totalTtc > 0 ? Math.round((orderPayments.total_paid / totalTtc) * 100) : 0;
  const invoices = orderPayments.invoices || [];
  const orderVatRate = order?.vat_rate ?? 20;

  // ---- Invoice form state (Phase 183) ----
  const [newInvoice, setNewInvoice] = useState({
    amount_ttc: "",
    vat_rate: String(orderVatRate),
    invoice_type: "situation",
    invoice_number: "",
    invoice_date: new Date(),
    notes: "",
  });
  const [savingInvoice, setSavingInvoice] = useState(false);

  const computedHt = useMemo(() => {
    const ttc = parseFloat(newInvoice.amount_ttc);
    const rate = parseFloat(newInvoice.vat_rate);
    if (Number.isNaN(ttc) || Number.isNaN(rate)) return null;
    if (rate <= 0) return ttc;
    return ttc / (1 + rate / 100);
  }, [newInvoice.amount_ttc, newInvoice.vat_rate]);

  const handleAddInvoice = async () => {
    const ttc = parseFloat(newInvoice.amount_ttc);
    if (!ttc || ttc <= 0) {
      toast.error("Montant TTC requis (> 0)");
      return;
    }
    try {
      setSavingInvoice(true);
      const payload = {
        amount_ttc: ttc,
        vat_rate: parseFloat(newInvoice.vat_rate) || 0,
        invoice_date: format(newInvoice.invoice_date, "yyyy-MM-dd"),
        invoice_type: newInvoice.invoice_type,
        invoice_number: newInvoice.invoice_number?.trim() || null,
        notes: newInvoice.notes?.trim() || null,
      };
      await api.addOrderInvoice(order.id, payload);
      toast.success("Facture enregistrée");
      setNewInvoice({
        amount_ttc: "",
        vat_rate: String(orderVatRate),
        invoice_type: "situation",
        invoice_number: "",
        invoice_date: new Date(),
        notes: "",
      });
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Supprimer la facture de ${formatCurrency(inv.amount_ttc)} TTC ?`)) return;
    try {
      await api.deleteOrderInvoice(inv.id);
      toast.success("Facture supprimée");
      await onRefresh?.();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  // Build merged history: payments + invoices sorted by date desc
  const history = useMemo(() => {
    const p = (orderPayments.payments || []).map((x) => ({
      kind: "payment",
      id: x.id,
      date: x.payment_date,
      raw: x,
    }));
    const i = invoices.map((x) => ({ kind: "invoice", id: x.id, date: x.invoice_date, raw: x }));
    return [...p, ...i].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [orderPayments.payments, invoices]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="payments-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Paiements &amp; Factures - {order?.reference}
          </DialogTitle>
          <DialogDescription>
            {order?.client_name} - Total TTC: {formatCurrency(totalTtc)}
            {order?.amount_ht != null && (
              <span className="text-xs text-muted-foreground ml-2">
                (HT : {formatCurrency(order.amount_ht)} • TVA {orderVatRate}%)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary — la jauge n'inclut PAS les factures */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg" data-testid="payment-summary">
            <div>
              <p className="text-sm text-muted-foreground">Total payé</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(orderPayments.total_paid)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Restant à payer</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(orderPayments.remaining)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Progression</p>
              <p className="text-lg font-bold">{progressPct}%</p>
            </div>
          </div>

          {/* Invoiced summary (info, hors jauge) */}
          {invoices.length > 0 && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg text-sm" data-testid="invoice-summary">
              <div>
                <p className="text-xs text-muted-foreground">Total facturé TTC</p>
                <p className="font-bold text-blue-800">{formatCurrency(orderPayments.total_invoiced_ttc || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total facturé HT</p>
                <p className="font-bold text-blue-800">{formatCurrency(orderPayments.total_invoiced_ht || 0)}</p>
              </div>
            </div>
          )}

          {/* Merged History */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Historique des paiements &amp; factures
            </Label>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground italic p-4 text-center bg-muted/30 rounded">
                Aucune entrée enregistrée
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => entry.kind === "payment" ? (
                  <div
                    key={`pay-${entry.id}`}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded border"
                    data-testid={`payment-row-${entry.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {entry.raw.payment_type === "acompte" ? "Paiement acompte" :
                           entry.raw.payment_type === "solde" ? "Paiement solde" : "Paiement"}
                        </Badge>
                        <span className="font-medium text-green-700">{formatCurrency(entry.raw.amount)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(entry.raw.payment_date)}
                        {entry.raw.notes && ` - ${entry.raw.notes}`}
                      </p>
                    </div>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => onDeletePayment(entry.raw.id)}
                      className="h-8 w-8"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ) : (
                  <div
                    key={`inv-${entry.id}`}
                    className="flex items-center justify-between p-3 bg-blue-50/60 rounded border border-blue-200"
                    data-testid={`invoice-row-${entry.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-blue-100 text-blue-800 text-xs hover:bg-blue-100">
                          <FileText className="h-3 w-3 mr-1" />
                          {INVOICE_TYPE_LABELS[entry.raw.invoice_type] || "Facture"}
                        </Badge>
                        {entry.raw.invoice_number && (
                          <span className="text-xs text-muted-foreground font-mono">№ {entry.raw.invoice_number}</span>
                        )}
                        <span className="font-medium text-blue-900">{formatCurrency(entry.raw.amount_ttc)} TTC</span>
                        <span className="text-xs text-muted-foreground">
                          (HT {formatCurrency(entry.raw.amount_ht)} • TVA {entry.raw.vat_rate}%)
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(entry.raw.invoice_date)}
                        {entry.raw.notes && ` - ${entry.raw.notes}`}
                      </p>
                    </div>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => handleDeleteInvoice(entry.raw)}
                      className="h-8 w-8"
                      data-testid={`delete-invoice-${entry.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Payment Form */}
          <div className="border-t pt-4 space-y-4">
            <Label className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-green-700" />
              Enregistrer un paiement reçu
            </Label>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment_amount">Montant (€)</Label>
                <Input
                  id="payment_amount"
                  type="number" step="0.01" min="0"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_type">Type</Label>
                <Select
                  value={newPayment.payment_type}
                  onValueChange={(v) => setNewPayment({ ...newPayment, payment_type: v })}
                >
                  <SelectTrigger data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acompte">Acompte</SelectItem>
                    <SelectItem value="intermediaire">Intermédiaire</SelectItem>
                    <SelectItem value="solde">Solde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date du paiement</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(newPayment.payment_date, "PPP", { locale: fr })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newPayment.payment_date}
                      onSelect={(d) => d && setNewPayment({ ...newPayment, payment_date: d })}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_notes">Notes (optionnel)</Label>
                <Input
                  id="payment_notes"
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                  placeholder="Référence, commentaire..."
                  data-testid="payment-notes-input"
                />
              </div>
            </div>

            <Button
              onClick={onAddPayment}
              className="w-full"
              disabled={!newPayment.amount}
              data-testid="add-payment-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter le paiement
            </Button>
          </div>

          {/* Add Invoice Form (Phase 183) */}
          <div className="border-t pt-4 space-y-4" data-testid="add-invoice-section">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-700" />
              Enregistrer une facture émise
              <span className="text-[10px] text-muted-foreground font-normal">
                (n&apos;impacte pas la jauge de paiement)
              </span>
            </Label>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-3 space-y-1">
                <Label htmlFor="invoice_ttc" className="text-xs">Montant TTC (€) *</Label>
                <Input
                  id="invoice_ttc"
                  type="number" step="0.01" min="0"
                  value={newInvoice.amount_ttc}
                  onChange={(e) => setNewInvoice({ ...newInvoice, amount_ttc: e.target.value })}
                  placeholder="0.00"
                  data-testid="invoice-ttc-input"
                />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label htmlFor="invoice_vat" className="text-xs">TVA (%)</Label>
                <Input
                  id="invoice_vat"
                  type="number" step="0.1" min="0" max="100"
                  value={newInvoice.vat_rate}
                  onChange={(e) => setNewInvoice({ ...newInvoice, vat_rate: e.target.value })}
                  data-testid="invoice-vat-input"
                />
              </div>
              <div className="md:col-span-3 space-y-1">
                <Label className="text-xs">Montant HT (calculé)</Label>
                <div
                  className="h-10 px-3 flex items-center text-sm border rounded bg-zinc-50 font-mono"
                  data-testid="invoice-ht-computed"
                >
                  {computedHt != null ? formatCurrency(computedHt) : "—"}
                </div>
              </div>
              <div className="md:col-span-4 space-y-1">
                <Label className="text-xs">Type de facture</Label>
                <Select
                  value={newInvoice.invoice_type}
                  onValueChange={(v) => setNewInvoice({ ...newInvoice, invoice_type: v })}
                >
                  <SelectTrigger data-testid="invoice-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acompte">Facture d&apos;acompte</SelectItem>
                    <SelectItem value="situation">Situation (1, 2, 3…)</SelectItem>
                    <SelectItem value="finale">Facture finale</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-4 space-y-1">
                <Label className="text-xs">Date de facturation</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="invoice-date-trigger">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(newInvoice.invoice_date, "PPP", { locale: fr })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newInvoice.invoice_date}
                      onSelect={(d) => d && setNewInvoice({ ...newInvoice, invoice_date: d })}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="md:col-span-4 space-y-1">
                <Label className="text-xs">N° de facture (optionnel)</Label>
                <Input
                  value={newInvoice.invoice_number}
                  onChange={(e) => setNewInvoice({ ...newInvoice, invoice_number: e.target.value })}
                  placeholder="FA-2026-001"
                  data-testid="invoice-number-input"
                />
              </div>
              <div className="md:col-span-4 space-y-1">
                <Label className="text-xs">Notes (optionnel)</Label>
                <Input
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                  placeholder="Commentaire..."
                  data-testid="invoice-notes-input"
                />
              </div>
            </div>

            <Button
              onClick={handleAddInvoice}
              className="w-full"
              variant="default"
              disabled={savingInvoice || !newInvoice.amount_ttc}
              data-testid="add-invoice-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              {savingInvoice ? "Enregistrement..." : "Enregistrer la facture"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
